import type {
  ReportGenerationAllowedOperator,
  ReportGenerationAuthorityEntityInput,
  ReportGenerationAuthorityFieldDefinition,
  ReportGenerationAuthorityFieldMetadata,
  ReportGenerationAuthorityTemplateInput,
  ReportGenerationExpansionTodoEntry,
} from './reportGenerationFieldAuthority';

const DOMAIN_PACKAGE = '@triedge/students-families-domain';
const APPSYNC_ROUTER = 'domainStudentsFamiliesApi';
const APPSYNC_FIELD = 'domainStudentsFamiliesQuery';

const TEXT_OPERATORS: ReportGenerationAllowedOperator[] = [
  'equals',
  'contains',
  'starts_with',
  'is_not',
];
const NUMBER_OPERATORS: ReportGenerationAllowedOperator[] = ['equals', 'gt', 'lt', 'between'];
const DATE_OPERATORS: ReportGenerationAllowedOperator[] = ['equals', 'before', 'after'];
const ENUM_OPERATORS: ReportGenerationAllowedOperator[] = ['is', 'is_not'];
const BOOLEAN_OPERATORS: ReportGenerationAllowedOperator[] = ['is_true', 'is_false'];

const operatorsByType: Record<
  ReportGenerationAuthorityFieldDefinition['type'],
  ReportGenerationAllowedOperator[]
> = {
  text: TEXT_OPERATORS,
  number: NUMBER_OPERATORS,
  date: DATE_OPERATORS,
  enum: ENUM_OPERATORS,
  boolean: BOOLEAN_OPERATORS,
};

const authority = (
  input: Omit<
    ReportGenerationAuthorityFieldMetadata,
    | 'scope'
    | 'status'
    | 'source'
    | 'domainPackage'
    | 'operationKind'
    | 'appSyncRouter'
    | 'appSyncField'
    | 'allowedOperators'
  > & {
    fieldType: ReportGenerationAuthorityFieldDefinition['type'];
  },
): ReportGenerationAuthorityFieldMetadata => ({
  scope: 'tuition',
  status: 'backed',
  source: 'generated',
  domainPackage: DOMAIN_PACKAGE,
  domainType: input.domainType,
  domainSchema: input.domainSchema,
  operationId: input.operationId,
  operationKind: 'query',
  appSyncRouter: APPSYNC_ROUTER,
  appSyncField: APPSYNC_FIELD,
  resultPath: input.resultPath,
  routeFamily: input.routeFamily,
  datasourceFamily: input.datasourceFamily,
  joinKey: input.joinKey,
  permission: input.permission,
  allowedOperators: operatorsByType[input.fieldType],
  sortSupported: input.sortSupported,
});

type AuthorityInput = Omit<Parameters<typeof authority>[0], 'fieldType'>;

const field = (
  input: Omit<ReportGenerationAuthorityFieldDefinition, 'authority'> & {
    authority: AuthorityInput;
  },
): ReportGenerationAuthorityFieldDefinition => ({
  id: input.id,
  label: input.label,
  type: input.type,
  enumValues: input.enumValues,
  authority: authority({
    ...input.authority,
    fieldType: input.type,
  }),
});

const familyCoreField = (
  input: Omit<ReportGenerationAuthorityFieldDefinition, 'authority'> & {
    resultPath: string;
    domainType?: string;
    domainSchema?: string;
    sortSupported?: boolean;
  },
): ReportGenerationAuthorityFieldDefinition => field({
  id: input.id,
  label: input.label,
  type: input.type,
  enumValues: input.enumValues,
  authority: {
    domainType: input.domainType ?? 'FamilyDetailCoreTree',
    domainSchema: input.domainSchema ?? 'FamilyDetailCoreTreeSchema',
    operationId: 'getFamilyDetailCoreTree',
    resultPath: input.resultPath,
    routeFamily: 'students-families',
    datasourceFamily: 'studentsFamilies.records',
    joinKey: 'family_id',
    permission: 'perm_view_students_families',
    sortSupported: input.sortSupported ?? true,
  },
});

const studentDashboardField = (
  input: Omit<ReportGenerationAuthorityFieldDefinition, 'authority'> & {
    resultPath: string;
    sortSupported?: boolean;
  },
): ReportGenerationAuthorityFieldDefinition => field({
  id: input.id,
  label: input.label,
  type: input.type,
  enumValues: input.enumValues,
  authority: {
    domainType: 'StudentsDashboardTree',
    domainSchema: 'StudentsDashboardTreeSchema',
    operationId: 'getStudentsDashboardTree',
    resultPath: input.resultPath,
    routeFamily: 'students-families',
    datasourceFamily: 'studentsFamilies.records',
    joinKey: 'family_id,student_id',
    permission: 'perm_view_students_families',
    sortSupported: input.sortSupported ?? true,
  },
});

const tuitionDashboardField = (
  input: Omit<ReportGenerationAuthorityFieldDefinition, 'authority'> & {
    resultPath: string;
    sortSupported?: boolean;
  },
): ReportGenerationAuthorityFieldDefinition => field({
  id: input.id,
  label: input.label,
  type: input.type,
  enumValues: input.enumValues,
  authority: {
    domainType: 'SchoolTuitionDashboardTree',
    domainSchema: 'SchoolTuitionDashboardTreeSchema',
    operationId: 'getSchoolTuitionDashboardTree',
    resultPath: input.resultPath,
    routeFamily: 'tuition',
    datasourceFamily: 'studentsFamilies.tuition',
    joinKey: 'family_id',
    permission: 'perm_view_financial_info',
    sortSupported: input.sortSupported ?? true,
  },
});

const tuitionLedgerField = (
  input: Omit<ReportGenerationAuthorityFieldDefinition, 'authority'> & {
    resultPath: string;
    domainType?: string;
    domainSchema?: string;
    sortSupported?: boolean;
  },
): ReportGenerationAuthorityFieldDefinition => field({
  id: input.id,
  label: input.label,
  type: input.type,
  enumValues: input.enumValues,
  authority: {
    domainType: input.domainType ?? 'SchoolTuitionFamilyLedgerTree',
    domainSchema: input.domainSchema ?? 'SchoolTuitionFamilyLedgerTreeSchema',
    operationId: 'getSchoolTuitionFamilyLedgerTree',
    resultPath: input.resultPath,
    routeFamily: 'tuition',
    datasourceFamily: 'studentsFamilies.tuition',
    joinKey: 'family_id',
    permission: 'perm_view_financial_info',
    sortSupported: input.sortSupported ?? true,
  },
});

export const TUITION_REPORT_GENERATION_ENTITY_AUTHORITY_INPUTS = [
  {
    id: 'families',
    label: 'Families',
    permission: 'perm_view_students_families',
    moduleLabel: 'Students & Families',
    joinFields: ['family_id'],
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    categories: [
      {
        name: 'Family Info',
        fields: [
          familyCoreField({
            id: 'family_name',
            label: 'Family name',
            type: 'text',
            resultPath: 'getFamilyDetailCoreTree.family.familyName',
            domainType: 'Family',
            domainSchema: 'FamilySchema',
          }),
          familyCoreField({
            id: 'primary_contact',
            label: 'Primary contact',
            type: 'text',
            resultPath: 'getFamilyDetailCoreTree.parentContacts[].firstName,lastName',
            domainType: 'ParentContact',
            domainSchema: 'ParentContactSchema',
          }),
          familyCoreField({
            id: 'email',
            label: 'Email',
            type: 'text',
            resultPath: 'getFamilyDetailCoreTree.parentContacts[].email',
            domainType: 'ParentContact',
            domainSchema: 'ParentContactSchema',
          }),
          familyCoreField({
            id: 'phone',
            label: 'Phone',
            type: 'text',
            resultPath: 'getFamilyDetailCoreTree.parentContacts[].phone|family.homePhone',
            domainType: 'ParentContact',
            domainSchema: 'ParentContactSchema',
          }),
          familyCoreField({
            id: 'address',
            label: 'Address',
            type: 'text',
            resultPath: 'getFamilyDetailCoreTree.family.address',
            domainType: 'Family',
            domainSchema: 'FamilySchema',
          }),
        ],
      },
      {
        name: 'Enrollment',
        fields: [
          familyCoreField({
            id: 'student_count',
            label: 'Student count',
            type: 'number',
            resultPath: 'getFamilyDetailCoreTree.students.length',
          }),
          familyCoreField({
            id: 'enrollment_status',
            label: 'Enrollment status',
            type: 'enum',
            enumValues: ['Active', 'Inactive', 'Withdrawn'],
            resultPath: 'getFamilyDetailCoreTree.family.status',
            domainType: 'Family',
            domainSchema: 'FamilySchema',
          }),
          familyCoreField({
            id: 'enrollment_date',
            label: 'Enrollment date',
            type: 'date',
            resultPath: 'getFamilyDetailCoreTree.students[].enrollmentDate',
            domainType: 'Student',
            domainSchema: 'StudentSchema',
          }),
          familyCoreField({
            id: 'rate_category',
            label: 'Rate category',
            type: 'text',
            resultPath: 'getFamilyDetailCoreTree.students[].rateCategory',
            domainType: 'Student',
            domainSchema: 'StudentSchema',
          }),
        ],
      },
      {
        name: 'Financial',
        fields: [
          field({
            id: 'total_balance',
            label: 'Total balance',
            type: 'number',
            authority: {
              domainType: 'FamilyDetailTuitionTree',
              domainSchema: 'FamilyDetailTuitionTreeSchema',
              operationId: 'getFamilyDetailTuitionTree',
              resultPath: 'getFamilyDetailTuitionTree.balance.netBalance',
              routeFamily: 'tuition',
              datasourceFamily: 'studentsFamilies.tuition',
              joinKey: 'family_id',
              permission: 'perm_view_financial_info',
              sortSupported: true,
            },
          }),
          familyCoreField({
            id: 'has_financial_aid',
            label: 'Has financial aid',
            type: 'boolean',
            resultPath: 'getFamilyDetailCoreTree.family.applicantId',
            domainType: 'Family',
            domainSchema: 'FamilySchema',
          }),
        ],
      },
    ],
  },
  {
    id: 'students',
    label: 'Students',
    permission: 'perm_view_students_families',
    moduleLabel: 'Students & Families',
    joinFields: ['family_id', 'student_id'],
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    categories: [
      {
        name: 'Demographics',
        fields: [
          studentDashboardField({
            id: 'first_name',
            label: 'First name',
            type: 'text',
            resultPath: 'getStudentsDashboardTree.families[].students[].firstName',
          }),
          studentDashboardField({
            id: 'last_name',
            label: 'Last name',
            type: 'text',
            resultPath: 'getStudentsDashboardTree.families[].students[].lastName',
          }),
          studentDashboardField({
            id: 'date_of_birth',
            label: 'Date of birth',
            type: 'date',
            resultPath: 'getStudentsDashboardTree.families[].students[].dateOfBirth',
          }),
          studentDashboardField({
            id: 'grade_level',
            label: 'Grade level',
            type: 'text',
            resultPath: 'getStudentsDashboardTree.families[].students[].grade',
          }),
          studentDashboardField({
            id: 'division',
            label: 'Division',
            type: 'enum',
            enumValues: ['Lower School', 'Middle School', 'Upper School'],
            resultPath: 'getStudentsDashboardTree.families[].students[].division',
          }),
        ],
      },
      {
        name: 'Status',
        fields: [
          studentDashboardField({
            id: 'student_status',
            label: 'Status',
            type: 'enum',
            enumValues: ['Active', 'Inactive', 'Graduated', 'Withdrawn'],
            resultPath: 'getStudentsDashboardTree.families[].students[].status',
          }),
          studentDashboardField({
            id: 'start_date',
            label: 'Start date',
            type: 'date',
            resultPath: 'getStudentsDashboardTree.families[].students[].enrollmentDate',
          }),
        ],
      },
    ],
  },
  {
    id: 'notes',
    label: 'Notes',
    permission: 'perm_view_students_families',
    moduleLabel: 'Students & Families',
    joinFields: ['family_id'],
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    categories: [
      {
        name: 'Note Details',
        fields: [
          field({
            id: 'note_date',
            label: 'Date',
            type: 'date',
            authority: {
              domainType: 'FamilyDetailNotesTree',
              domainSchema: 'FamilyDetailNotesTreeSchema',
              operationId: 'getFamilyDetailNotesTree',
              resultPath: 'getFamilyDetailNotesTree.timeline.items[].createdAt',
              routeFamily: 'students-families',
              datasourceFamily: 'studentsFamilies.records',
              joinKey: 'family_id,student_id',
              permission: 'perm_view_students_families',
              sortSupported: true,
            },
          }),
          field({
            id: 'note_author',
            label: 'Author',
            type: 'text',
            authority: {
              domainType: 'FamilyInteraction',
              domainSchema: 'FamilyInteractionSchema',
              operationId: 'getFamilyDetailNotesTree',
              resultPath: 'getFamilyDetailNotesTree.timeline.items[].authorName',
              routeFamily: 'students-families',
              datasourceFamily: 'studentsFamilies.records',
              joinKey: 'family_id,student_id',
              permission: 'perm_view_students_families',
              sortSupported: true,
            },
          }),
          field({
            id: 'note_category',
            label: 'Category',
            type: 'text',
            authority: {
              domainType: 'FamilyInteraction',
              domainSchema: 'FamilyInteractionSchema',
              operationId: 'getFamilyDetailNotesTree',
              resultPath: 'getFamilyDetailNotesTree.timeline.items[].category',
              routeFamily: 'students-families',
              datasourceFamily: 'studentsFamilies.records',
              joinKey: 'family_id,student_id',
              permission: 'perm_view_students_families',
              sortSupported: true,
            },
          }),
          field({
            id: 'note_content',
            label: 'Content',
            type: 'text',
            authority: {
              domainType: 'FamilyInteraction',
              domainSchema: 'FamilyInteractionSchema',
              operationId: 'getFamilyDetailNotesTree',
              resultPath: 'getFamilyDetailNotesTree.timeline.items[].content',
              routeFamily: 'students-families',
              datasourceFamily: 'studentsFamilies.records',
              joinKey: 'family_id,student_id',
              permission: 'perm_view_students_families',
              sortSupported: false,
            },
          }),
        ],
      },
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    permission: 'perm_view_students_families',
    moduleLabel: 'Students & Families',
    joinFields: ['family_id'],
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    categories: [
      {
        name: 'Document Info',
        fields: [
          field({
            id: 'doc_name',
            label: 'Document name',
            type: 'text',
            authority: {
              domainType: 'StudentDocument',
              domainSchema: 'StudentDocumentSchema',
              operationId: 'getFamilyDetailDocumentsTree',
              resultPath: 'getFamilyDetailDocumentsTree.documents.items[].name',
              routeFamily: 'students-families',
              datasourceFamily: 'studentsFamilies.records',
              joinKey: 'family_id,student_id',
              permission: 'perm_view_students_families',
              sortSupported: true,
            },
          }),
          field({
            id: 'doc_type',
            label: 'Type',
            type: 'text',
            authority: {
              domainType: 'StudentDocument',
              domainSchema: 'StudentDocumentSchema',
              operationId: 'getFamilyDetailDocumentsTree',
              resultPath: 'getFamilyDetailDocumentsTree.documents.items[].category',
              routeFamily: 'students-families',
              datasourceFamily: 'studentsFamilies.records',
              joinKey: 'family_id,student_id',
              permission: 'perm_view_students_families',
              sortSupported: true,
            },
          }),
          field({
            id: 'upload_date',
            label: 'Upload date',
            type: 'date',
            authority: {
              domainType: 'StudentDocument',
              domainSchema: 'StudentDocumentSchema',
              operationId: 'getFamilyDetailDocumentsTree',
              resultPath: 'getFamilyDetailDocumentsTree.documents.items[].uploadedAt',
              routeFamily: 'students-families',
              datasourceFamily: 'studentsFamilies.records',
              joinKey: 'family_id,student_id',
              permission: 'perm_view_students_families',
              sortSupported: true,
            },
          }),
          field({
            id: 'doc_status',
            label: 'Status',
            type: 'enum',
            enumValues: ['Pending', 'Approved', 'Rejected'],
            authority: {
              domainType: 'StudentDocument',
              domainSchema: 'StudentDocumentSchema',
              operationId: 'getFamilyDetailDocumentsTree',
              resultPath: 'getFamilyDetailDocumentsTree.documents.items[].status|verified',
              routeFamily: 'students-families',
              datasourceFamily: 'studentsFamilies.records',
              joinKey: 'family_id,student_id',
              permission: 'perm_view_students_families',
              sortSupported: true,
            },
          }),
        ],
      },
    ],
  },
  {
    id: 'installments',
    label: 'Installments',
    permission: 'perm_view_financial_info',
    moduleLabel: 'Tuition',
    joinFields: ['family_id'],
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    categories: [
      {
        name: 'Schedule Details',
        fields: [
          tuitionDashboardField({
            id: 'due_date',
            label: 'Due date',
            type: 'date',
            resultPath: 'getSchoolTuitionDashboardTree.families[].nextDueDate',
          }),
          tuitionDashboardField({
            id: 'plan_name',
            label: 'Plan name',
            type: 'text',
            resultPath: 'getSchoolTuitionDashboardTree.families[].planLabel',
          }),
          tuitionDashboardField({
            id: 'academic_year',
            label: 'Academic year',
            type: 'text',
            resultPath: 'getSchoolTuitionDashboardTree.academicYear',
          }),
        ],
      },
      {
        name: 'Payment Status',
        fields: [
          tuitionDashboardField({
            id: 'payment_status',
            label: 'Status',
            type: 'enum',
            enumValues: ['Paid', 'Partial', 'Unpaid', 'Overdue'],
            resultPath: 'getSchoolTuitionDashboardTree.families[].statusLabel,currentDueCents,overdueCents',
          }),
          tuitionDashboardField({
            id: 'is_overdue',
            label: 'Is overdue',
            type: 'boolean',
            resultPath: 'getSchoolTuitionDashboardTree.families[].overdueCents',
          }),
        ],
      },
      {
        name: 'Amounts',
        fields: [
          tuitionDashboardField({
            id: 'amount_due',
            label: 'Amount due',
            type: 'number',
            resultPath: 'getSchoolTuitionDashboardTree.families[].currentDueCents,overdueCents',
          }),
          tuitionDashboardField({
            id: 'amount_paid',
            label: 'Amount paid',
            type: 'number',
            resultPath: 'getSchoolTuitionDashboardTree.families[].totalCreditsCents',
          }),
          tuitionDashboardField({
            id: 'balance_remaining',
            label: 'Balance remaining',
            type: 'number',
            resultPath: 'getSchoolTuitionDashboardTree.families[].netBalanceCents',
          }),
        ],
      },
    ],
  },
  {
    id: 'payments',
    label: 'Payments',
    permission: 'perm_view_financial_info',
    moduleLabel: 'Tuition',
    joinFields: ['family_id', 'payment_id'],
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    categories: [
      {
        name: 'Payment Info',
        fields: [
          tuitionLedgerField({
            id: 'payment_date',
            label: 'Payment date',
            type: 'date',
            resultPath: 'getSchoolTuitionFamilyLedgerTree.ledgerEntries[entryType=payment|refund].internalEffectiveDate|createdAt',
            domainType: 'SchoolTuitionTransaction',
            domainSchema: 'SchoolTuitionTransactionSchema',
          }),
          tuitionLedgerField({
            id: 'payment_amount',
            label: 'Amount',
            type: 'number',
            resultPath: 'getSchoolTuitionFamilyLedgerTree.ledgerEntries[entryType=payment|refund].amountCents',
            domainType: 'SchoolTuitionTransaction',
            domainSchema: 'SchoolTuitionTransactionSchema',
          }),
          tuitionLedgerField({
            id: 'payment_method',
            label: 'Method',
            type: 'enum',
            enumValues: ['ACH', 'Credit Card', 'Check', 'Cash', 'Wire', 'Recorded'],
            resultPath: 'getSchoolTuitionFamilyLedgerTree.ledgerEntries[entryType=payment|refund].source',
            domainType: 'SchoolTuitionTransaction',
            domainSchema: 'SchoolTuitionTransactionSchema',
          }),
          tuitionLedgerField({
            id: 'reference_number',
            label: 'Reference number',
            type: 'text',
            resultPath: 'getSchoolTuitionFamilyLedgerTree.ledgerEntries[entryType=payment|refund].referenceCode',
            domainType: 'SchoolTuitionTransaction',
            domainSchema: 'SchoolTuitionTransactionSchema',
          }),
        ],
      },
      {
        name: 'Allocation',
        fields: [
          tuitionLedgerField({
            id: 'allocated_to',
            label: 'Allocated to',
            type: 'text',
            resultPath: 'getSchoolTuitionFamilyLedgerTree.ledgerEntries[entryType=payment|refund].studentId|family.familyName',
            domainType: 'SchoolTuitionTransaction',
            domainSchema: 'SchoolTuitionTransactionSchema',
          }),
          tuitionLedgerField({
            id: 'payment_status_p',
            label: 'Status',
            type: 'enum',
            enumValues: ['Completed', 'Pending', 'Failed', 'Refunded'],
            resultPath: 'getSchoolTuitionFamilyLedgerTree.ledgerEntries[entryType=payment|refund].status',
            domainType: 'SchoolTuitionTransaction',
            domainSchema: 'SchoolTuitionTransactionSchema',
          }),
        ],
      },
    ],
  },
  {
    id: 'tuition_accounts',
    label: 'Tuition Accounts',
    permission: 'perm_view_financial_info',
    moduleLabel: 'Tuition',
    joinFields: ['family_id'],
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    categories: [
      {
        name: 'Account Summary',
        fields: [
          tuitionDashboardField({
            id: 'annual_tuition',
            label: 'Annual tuition',
            type: 'number',
            resultPath: 'getSchoolTuitionDashboardTree.families[].totalChargesCents',
          }),
          tuitionDashboardField({
            id: 'total_charged',
            label: 'Total charged',
            type: 'number',
            resultPath: 'getSchoolTuitionDashboardTree.families[].totalChargesCents',
          }),
          tuitionDashboardField({
            id: 'total_paid_ta',
            label: 'Total paid',
            type: 'number',
            resultPath: 'getSchoolTuitionDashboardTree.families[].totalCreditsCents',
          }),
          tuitionDashboardField({
            id: 'account_balance',
            label: 'Account balance',
            type: 'number',
            resultPath: 'getSchoolTuitionDashboardTree.families[].netBalanceCents',
          }),
          tuitionDashboardField({
            id: 'account_status',
            label: 'Account status',
            type: 'enum',
            enumValues: ['Current', 'Paid in Full', 'Overdue', 'Delinquent'],
            resultPath: 'getSchoolTuitionDashboardTree.families[].statusLabel',
          }),
        ],
      },
    ],
  },
  {
    id: 'ledger_entries',
    label: 'Ledger Entries',
    permission: 'perm_view_financial_info',
    moduleLabel: 'Tuition',
    joinFields: ['family_id'],
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    categories: [
      {
        name: 'Entry Details',
        fields: [
          tuitionLedgerField({
            id: 'entry_date',
            label: 'Entry date',
            type: 'date',
            resultPath: 'getSchoolTuitionFamilyLedgerTree.ledgerEntries[].internalEffectiveDate|createdAt',
            domainType: 'SchoolTuitionTransaction',
            domainSchema: 'SchoolTuitionTransactionSchema',
          }),
          tuitionLedgerField({
            id: 'entry_type',
            label: 'Type',
            type: 'enum',
            enumValues: ['Charge', 'Payment', 'Credit', 'Adjustment', 'Refund'],
            resultPath: 'getSchoolTuitionFamilyLedgerTree.ledgerEntries[].entryType',
            domainType: 'SchoolTuitionTransaction',
            domainSchema: 'SchoolTuitionTransactionSchema',
          }),
          tuitionLedgerField({
            id: 'description',
            label: 'Description',
            type: 'text',
            resultPath: 'getSchoolTuitionFamilyLedgerTree.ledgerEntries[].description',
            domainType: 'SchoolTuitionTransaction',
            domainSchema: 'SchoolTuitionTransactionSchema',
            sortSupported: false,
          }),
          tuitionLedgerField({
            id: 'debit',
            label: 'Debit',
            type: 'number',
            resultPath: 'getSchoolTuitionFamilyLedgerTree.ledgerEntries[].amountCents[positive]',
            domainType: 'SchoolTuitionTransaction',
            domainSchema: 'SchoolTuitionTransactionSchema',
          }),
          tuitionLedgerField({
            id: 'credit',
            label: 'Credit',
            type: 'number',
            resultPath: 'getSchoolTuitionFamilyLedgerTree.ledgerEntries[].amountCents[negative]',
            domainType: 'SchoolTuitionTransaction',
            domainSchema: 'SchoolTuitionTransactionSchema',
          }),
        ],
      },
    ],
  },
  {
    id: 'contracts',
    label: 'Contracts',
    permission: 'perm_view_financial_info',
    moduleLabel: 'Tuition',
    joinFields: ['family_id'],
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    categories: [
      {
        name: 'Contract Info',
        fields: [
          tuitionLedgerField({
            id: 'contract_year',
            label: 'Academic year',
            type: 'text',
            resultPath: 'getSchoolTuitionFamilyLedgerTree.academicYear',
          }),
          tuitionLedgerField({
            id: 'contract_status',
            label: 'Status',
            type: 'enum',
            enumValues: ['Draft', 'Sent', 'Signed', 'Expired'],
            resultPath: 'getSchoolTuitionFamilyLedgerTree.documents[kind=contract].status',
            domainType: 'SchoolTuitionDocument',
            domainSchema: 'SchoolTuitionDocumentSchema',
          }),
          tuitionLedgerField({
            id: 'signed_date',
            label: 'Signed date',
            type: 'date',
            resultPath: 'getSchoolTuitionFamilyLedgerTree.documents[kind=contract].uploadedAt',
            domainType: 'SchoolTuitionDocument',
            domainSchema: 'SchoolTuitionDocumentSchema',
          }),
          tuitionLedgerField({
            id: 'contract_amount',
            label: 'Contract amount',
            type: 'number',
            resultPath: 'getSchoolTuitionFamilyLedgerTree.family.totalChargesCents',
            domainType: 'SchoolTuitionFamily',
            domainSchema: 'SchoolTuitionFamilySchema',
          }),
        ],
      },
    ],
  },
] as const satisfies readonly ReportGenerationAuthorityEntityInput[];

export const TUITION_REPORT_GENERATION_TEMPLATE_AUTHORITY_INPUTS = [
  {
    id: 'aging-report',
    name: 'Aging Report',
    description: 'Outstanding balances by 30/60/90/120+ day buckets',
    moduleLabel: 'Tuition',
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    entities: ['installments', 'families'],
    fields: ['family_name', 'due_date', 'amount_due', 'balance_remaining', 'is_overdue', 'academic_year'],
  },
  {
    id: 'collection-summary',
    name: 'Collection Summary',
    description: 'Total collections by period with payment method breakdown',
    moduleLabel: 'Tuition',
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    entities: ['payments'],
    fields: ['payment_date', 'payment_amount', 'payment_method'],
  },
  {
    id: 'payment-history',
    name: 'Payment History',
    description: 'Complete payment transaction log by family',
    moduleLabel: 'Tuition',
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    entities: ['payments', 'families'],
    fields: ['family_name', 'payment_date', 'payment_amount', 'payment_method', 'reference_number'],
  },
  {
    id: 'outstanding-balances',
    name: 'Outstanding Balances',
    description: 'Families with unpaid balances and account status',
    moduleLabel: 'Tuition',
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    entities: ['tuition_accounts', 'families'],
    fields: ['family_name', 'account_balance', 'account_status', 'annual_tuition'],
    conditions: [{
      id: 'template-outstanding-balances-positive-balance',
      fieldId: 'account_balance',
      fieldLabel: 'Account balance',
      fieldType: 'number',
      operator: 'gt',
      value: '0',
    }],
    sorts: [{
      fieldId: 'account_balance',
      fieldLabel: 'Account balance',
      direction: 'desc',
    }],
  },
  {
    id: 'tuition-ledger-review',
    name: 'Tuition Ledger Review',
    description: 'Ledger entry detail by family for reconciliation and audit review',
    moduleLabel: 'Tuition',
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    entities: ['ledger_entries', 'families'],
    fields: ['family_name', 'entry_date', 'entry_type', 'description', 'debit', 'credit'],
  },
  {
    id: 'contract-status',
    name: 'Contract Status',
    description: 'Tuition contract amount, status, and signature timing by family',
    moduleLabel: 'Tuition',
    authorityScope: 'tuition',
    authorityStatus: 'backed',
    entities: ['contracts', 'families'],
    fields: ['family_name', 'contract_year', 'contract_status', 'signed_date', 'contract_amount'],
  },
] as const satisfies readonly ReportGenerationAuthorityTemplateInput[];

export const TUITION_REPORT_GENERATION_FIRST_WAVE_ENTITY_IDS =
  TUITION_REPORT_GENERATION_ENTITY_AUTHORITY_INPUTS.map((entity) => entity.id);

export const TUITION_REPORT_GENERATION_FIRST_WAVE_FIELD_IDS =
  TUITION_REPORT_GENERATION_ENTITY_AUTHORITY_INPUTS.flatMap((entity) =>
    entity.categories.flatMap((category) => category.fields.map((fieldValue) => fieldValue.id)),
  );

export const TUITION_REPORT_GENERATION_FIELD_AUTHORITY_BY_FIELD_ID = Object.freeze(
  Object.fromEntries(
    TUITION_REPORT_GENERATION_ENTITY_AUTHORITY_INPUTS.flatMap((entity) =>
      entity.categories.flatMap((category) =>
        category.fields.map((fieldValue) => [
          fieldValue.id,
          {
            entityId: entity.id,
            field: fieldValue,
            authority: fieldValue.authority,
          },
        ]),
      ),
    ),
  ),
);

export const REPORT_FIELD_AUTHORITY_EXPANSION_TODO = [
  {
    domain: 'students-families',
    entityId: 'families',
    status: 'backed',
    reason: 'First-wave join anchor backed by FamilySchema and family/core tree operations.',
    requiredOperationIds: ['getStudentsDashboardTree', 'getFamilyDetailCoreTree'],
  },
  {
    domain: 'students-families',
    entityId: 'students',
    status: 'backed',
    reason: 'First-wave join anchor backed by StudentSchema and students dashboard/core tree operations.',
    requiredOperationIds: ['getStudentsDashboardTree', 'getFamilyDetailCoreTree'],
  },
  {
    domain: 'students-families',
    entityId: 'notes',
    status: 'backed',
    reason: 'First-wave notes are backed by FamilyInteractionSchema through getFamilyDetailNotesTree.',
    requiredOperationIds: ['getFamilyDetailNotesTree'],
  },
  {
    domain: 'students-families',
    entityId: 'documents',
    status: 'backed',
    reason: 'First-wave documents are backed by StudentDocumentSchema through getFamilyDetailDocumentsTree.',
    requiredOperationIds: ['getFamilyDetailDocumentsTree'],
  },
  {
    domain: 'tuition',
    entityId: 'installments',
    status: 'backed',
    reason: 'First-wave due/status/amount fields are backed by SchoolTuitionDashboardTree family snapshots.',
    requiredOperationIds: ['getSchoolTuitionDashboardTree'],
  },
  {
    domain: 'tuition',
    entityId: 'payments',
    status: 'backed',
    reason: 'First-wave payment rows are backed by SchoolTuitionTransaction entries in family ledger trees.',
    requiredOperationIds: ['getSchoolTuitionFamilyLedgerTree'],
  },
  {
    domain: 'tuition',
    entityId: 'tuition_accounts',
    status: 'backed',
    reason: 'First-wave account summary is backed by SchoolTuitionDashboardTree family snapshots.',
    requiredOperationIds: ['getSchoolTuitionDashboardTree'],
  },
  {
    domain: 'tuition',
    entityId: 'ledger_entries',
    status: 'backed',
    reason: 'First-wave ledger entries are backed by SchoolTuitionTransaction ledger rows.',
    requiredOperationIds: ['getSchoolTuitionFamilyLedgerTree'],
  },
  {
    domain: 'tuition',
    entityId: 'contracts',
    status: 'backed',
    reason: 'First-wave contract rows are backed by contract documents in SchoolTuitionFamilyLedgerTree.',
    requiredOperationIds: ['getSchoolTuitionFamilyLedgerTree'],
  },
  {
    domain: 'admissions',
    entityId: 'admissions_applications',
    status: 'candidate',
    reason: 'Legacy Run Reports catalog exists, but field-level authority must be regenerated from admissions router contracts.',
    requiredOperationIds: ['listApplicationsPage', 'getApplicationDetail'],
  },
  {
    domain: 'admissions',
    entityId: 'enrollment_records',
    status: 'candidate',
    reason: 'Enrollment conversion fields need admissions-to-students join authority before promotion.',
    requiredOperationIds: ['listApplicationsPage', 'getApplicationDetail'],
  },
  {
    domain: 'financial-aid',
    entityId: 'fa_applications',
    status: 'candidate',
    reason: 'Legacy Run Reports catalog exists, but field-level authority must be regenerated from financial-aid review contracts.',
    requiredOperationIds: ['listFinancialAidApplicationsForReview', 'getFinancialAidApplicationForReview'],
  },
  {
    domain: 'financial-aid',
    entityId: 'scholarships',
    status: 'blocked-missing-operation',
    reason: 'Scholarship-style rows are currently derived from financial-aid decisions and need an owned scholarship/award operation.',
    requiredOperationIds: ['listStudentAidAwards'],
  },
  {
    domain: 'attendance',
    entityId: 'attendance_records',
    status: 'blocked-missing-operation',
    reason: 'No Run Reports field authority is mapped for attendance operations in this wave.',
    requiredOperationIds: ['listAttendanceRecords'],
  },
  {
    domain: 'faculty',
    entityId: 'faculty_members',
    status: 'blocked-missing-operation',
    reason: 'No Run Reports field authority is mapped for faculty operations in this wave.',
    requiredOperationIds: ['listFacultyMembers'],
  },
  {
    domain: 'payroll',
    entityId: 'payroll_runs',
    status: 'blocked-missing-operation',
    reason: 'Payroll fields require a dedicated permission and router field authority before selection.',
    requiredOperationIds: ['listPayrollRuns'],
  },
  {
    domain: 'settings',
    entityId: 'staff_users',
    status: 'candidate',
    reason: 'Settings/user-management reporting needs separate privacy review before selectable fields are generated.',
    requiredOperationIds: ['listStaffUsers'],
  },
  {
    domain: 'payments',
    entityId: 'payment_provider_events',
    status: 'candidate',
    reason: 'Provider events are support evidence for tuition but are not selectable report fields in this wave.',
    requiredOperationIds: ['getPaymentControlPlaneReadiness'],
  },
  {
    domain: 'academics',
    entityId: 'academic_records',
    status: 'blocked-missing-operation',
    reason: 'No Run Reports field authority is mapped for academic records in this wave.',
    requiredOperationIds: ['listAcademicRecords'],
  },
  {
    domain: 'substitutes',
    entityId: 'substitute_assignments',
    status: 'blocked-missing-operation',
    reason: 'No Run Reports field authority is mapped for substitute operations in this wave.',
    requiredOperationIds: ['listSubstituteSchedule'],
  },
  {
    domain: 'camps',
    entityId: 'camp_registrations',
    status: 'blocked-missing-operation',
    reason: 'No Run Reports field authority is mapped for camp operations in this wave.',
    requiredOperationIds: ['listCampRegistrations'],
  },
  {
    domain: 'donations',
    entityId: 'donation_records',
    status: 'blocked-missing-operation',
    reason: 'No Run Reports field authority is mapped for donation operations in this wave.',
    requiredOperationIds: ['listDonationRecords'],
  },
  {
    domain: 'parent-portal',
    entityId: 'parent_portal_records',
    status: 'blocked-missing-operation',
    reason: 'Parent-facing report fields require parent-scope permission and subject envelope rules before selection.',
    requiredOperationIds: ['listParentPortalRecords'],
  },
] as const satisfies readonly ReportGenerationExpansionTodoEntry[];
